import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Star, Heart, X, Send, Check } from 'lucide-react';
import { useStores } from '@/store/dataStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = 'bigtime_feedback_sent_at';

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1 justify-center" data-testid="fb-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`fb-star-${n}`}
          className="p-1 group"
          aria-label={`${n} estrellas`}
        >
          <Star
            size={30}
            strokeWidth={2}
            className={n <= value
              ? 'text-yellow-400 fill-yellow-400 drop-shadow'
              : 'text-slate-300 hover:text-yellow-300 transition'}
          />
        </button>
      ))}
    </div>
  );
}

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [storeId, setStoreId] = useState('');
  const [comment, setComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const { stores } = useStores();

  const submit = async (e) => {
    e.preventDefault();
    if (stars < 1) { setErr('Elige una calificación primero.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/feedback`, {
        store_id: storeId || null,
        stars,
        comment: comment.trim(),
        author_name: authorName.trim(),
      });
      setDone(true);
      localStorage.setItem(SEEN_KEY, Date.now().toString());
      setTimeout(() => { setOpen(false); setDone(false); setStars(0); setComment(''); setAuthorName(''); }, 2200);
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'No se pudo enviar. Intenta de nuevo.');
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="feedback-launcher"
          aria-label="Enviar opinión"
          className="fixed z-[55] bottom-[calc(var(--fab-base)+104px)] right-3 sm:right-auto sm:bottom-28 sm:left-6 flex items-center gap-2 bg-white text-[#1B2A4A] border-2 border-[#1B2A4A] hover:bg-yellow-50 rounded-full p-1 sm:pl-3 sm:pr-4 sm:py-2 shadow-xl transition"
        >
          <span className="w-8 h-8 rounded-full bg-yellow-300 flex items-center justify-center">
            <Heart size={16} className="text-[#1B2A4A]" fill="#1B2A4A" />
          </span>
          <span className="text-xs font-bold hidden sm:block">¿Cómo fue tu experiencia?</span>
        </button>
      )}

      {open && (
        <div
          className="fixed z-[60] bottom-[calc(var(--fab-base)+4px)] left-2 sm:bottom-24 sm:left-6 w-[min(96vw,380px)] max-h-[calc(100dvh-var(--fab-base)-16px)] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="feedback-window"
        >
          <div className="bg-gradient-to-br from-[#1B2A4A] to-[#173a5c] text-white p-3.5 flex items-center gap-2">
            <Heart size={18} fill="#F8D13F" className="text-yellow-300" />
            <div className="flex-1">
              <div className="font-bold text-sm">¿Cómo fue tu experiencia?</div>
              <div className="text-[11px] text-white/70">Tu opinión mejora BIGTIME</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="feedback-close"
              className="w-10 h-10 -mr-2 flex items-center justify-center rounded-full hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
          </div>

          {done ? (
            <div className="p-6 text-center" data-testid="feedback-thanks">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <Check size={30} className="text-emerald-600" strokeWidth={3} />
              </div>
              <h4 className="font-bold text-lg text-[#1B2A4A]">¡Gracias!</h4>
              <p className="text-sm text-slate-500 mt-1">Tu opinión fue registrada. Con ella crecemos.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3" data-testid="feedback-form">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1 text-center">Calificación</label>
                <StarPicker value={stars} onChange={setStars} />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Tienda que te atendió</label>
                <select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-md h-9 px-2"
                  data-testid="fb-store"
                >
                  <option value="">— Selecciona —</option>
                  {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Tu nombre (opcional)</label>
                <Input value={authorName} onChange={(e) => setAuthorName(e.target.value)} className="h-9" data-testid="fb-name" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Comentario</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Cuéntanos qué tal…"
                  maxLength={600}
                  rows={3}
                  className="w-full text-sm border border-slate-200 rounded-md p-2 resize-none"
                  data-testid="fb-comment"
                />
              </div>

              {err && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="fb-error">{err}</p>}

              <Button
                type="submit"
                disabled={busy}
                data-testid="fb-submit"
                className="w-full h-10 rounded-full bg-[#1B2A4A] text-yellow-300 hover:bg-[#141f38] font-bold"
              >
                <Send size={14} className="mr-1" /> {busy ? 'Enviando…' : 'Enviar opinión'}
              </Button>
              <p className="text-[10px] text-slate-400 text-center">Solo el Administrador puede ver los comentarios.</p>
            </form>
          )}
        </div>
      )}
    </>
  );
}
